import { useEffect, useState } from 'preact/hooks';
import { CheckoutShell } from '@/components/CheckoutShell';
import { buildLocalAliasUrl, buildSecureUrl, navigate, probeHttpsReachable, type SecureResumePayload } from '@/secureRedirect';
import { hasDevPort, isLocalhostAlias, LOCAL_DEV_DOCS_URL, TEST_MODE_DOCS_URL } from '@/localDev';
import { logWarn } from '@/sdkConsole';
import { t, useT } from '@/i18n/t';

interface Props {
  // The selection parked in the ezsm_resume param across the https reload.
  payload: SecureResumePayload;
  // Surfaced only when the parent flow is dismissible.
  onDismiss?: () => void;
}

// SecureRedirectStep is shown when checkout is started on a page loaded over
// plain http: Stripe.js refuses live-mode initialization outside a secure
// context, so the embedded payment step can never run there. The visitor gets
// a reassuring explanation and a CTA that reloads the page over https with
// their selection parked in the URL; bootstrap resumes checkout from it.
//
// Copy rules (see docs/plans/2026-08-01-secure-checkout-redirect.md): no
// protocol jargon, reassure never alarm, neutral toward the publisher, one
// primary action.
export function SecureRedirectStep({ payload, onDismiss }: Props) {
  useT();
  // null = probe in flight; the CTA stays disabled until reachability is known.
  const [reachable, setReachable] = useState<boolean | null>(null);
  const [navigating, setNavigating] = useState(false);
  // A re-probe after an unavailable verdict keeps the unavailable copy on
  // screen (flipping back to the default copy mid-retry reads as a glitch) and
  // locks the button against a double click.
  const [retrying, setRetrying] = useState(false);
  // Set when a re-probe fails, so the retry reads as answered rather than dead.
  const [retryFailed, setRetryFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void probeHttpsReachable().then((ok) => {
      if (!cancelled) {
        setReachable(ok);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleContinue = () => {
    setNavigating(true);
    logWarn('redirecting to secure checkout');
    navigate(buildSecureUrl(payload));
  };

  // "Try again" on the unavailable screen re-probes and only navigates if the
  // site's https endpoint answers this time — a transient network blip must not
  // strand the visitor, but a dead https endpoint must not get a dead redirect.
  const handleRetry = async () => {
    setRetrying(true);
    setRetryFailed(false);
    const ok = await probeHttpsReachable();
    if (ok) {
      handleContinue();
      return;
    }
    setRetrying(false);
    setRetryFailed(true);
  };

  // The alias CTA on the dev variant: *.localhost is a secure context in
  // Chrome/Firefox, so live checkout runs there over HTTP — one click to the
  // fix, with the selection parked in the URL so checkout resumes in place.
  const handleOpenAlias = () => {
    logWarn('redirecting to the .localhost alias for live checkout');
    navigate(buildLocalAliasUrl(payload));
  };

  // devGuidance is the probe-failed state on a non-standard port: near-
  // certainly a local dev server with no TLS, where re-probing can never
  // succeed — so there is no "Try again" here, the CTA is the fix. Hardcoded
  // English: the i18n key-drift guard requires every key in all shipped
  // locales, and this state is developer-facing.
  function devGuidance() {
    const host = window.location.hostname;
    // Already on a *.localhost alias in a browser that doesn't treat it as a
    // secure context — suggesting another alias would double the suffix, so
    // test mode is the only path there.
    const onAlias = isLocalhostAlias(host);
    return (
      <>
        <h2 class="sm-title">Live checkout requires HTTPS</h2>
        {onAlias ? (
          <p class="sm-subtitle" role="alert">
            This browser doesn't treat <code>{host}</code> as a secure connection, so live checkout can't run
            here. Use <a href={TEST_MODE_DOCS_URL} target="_blank" rel="noopener noreferrer">test mode</a> to
            verify your integration. See{' '}
            <a href={LOCAL_DEV_DOCS_URL} target="_blank" rel="noopener noreferrer">local development guidance</a>
          </p>
        ) : (
          <>
            <p class="sm-subtitle" role="alert">
              This page is served over HTTP by a local dev server. Live checkout requires a secure HTTPS
              connection.
            </p>
            <div class="sm-info-panel">
              <p>
                To verify your integration, use{' '}
                <a href={TEST_MODE_DOCS_URL} target="_blank" rel="noopener noreferrer">test mode</a>. See{' '}
                <a href={LOCAL_DEV_DOCS_URL} target="_blank" rel="noopener noreferrer">local development guidance</a>
              </p>
            </div>
            <button class="sm-btn sm-btn-primary" type="button" onClick={handleOpenAlias}>
              Open {host}.localhost:{window.location.port}
            </button>
          </>
        )}
      </>
    );
  }

  // body picks the variant by early return: probe pending/reachable → the
  // default redirect pitch; probe failed on a dev port → developer guidance;
  // probe failed on a standard port → the consumer unavailable state.
  function body() {
    if (reachable !== false) {
      return (
        <>
          <h2 class="sm-title">{t('secureRedirect.title')}</h2>
          <p class="sm-subtitle">
            {t('secureRedirect.subtitle')}
          </p>
          <button class="sm-btn sm-btn-primary" type="button" disabled={reachable === null || navigating} onClick={handleContinue}>
            {navigating && <span class="sm-btn-spinner" aria-hidden="true" />}
            {t('secureRedirect.continue')}
          </button>
        </>
      );
    }
    if (hasDevPort()) {
      return devGuidance();
    }
    return (
      <>
        <h2 class="sm-title">{t('secureRedirect.unavailableTitle')}</h2>
        <p class="sm-subtitle" role="alert">
          {t('secureRedirect.unavailableSubtitle')}
        </p>
        <button class="sm-btn sm-btn-primary" type="button" disabled={retrying} onClick={handleRetry}>
          {retrying && <span class="sm-btn-spinner" aria-hidden="true" />}
          {t('checkout.tryAgain')}
        </button>
        {retryFailed && (
          <p class="sm-note" role="status">
            {t('secureRedirect.stillUnavailable')}
          </p>
        )}
      </>
    );
  }

  return (
    <CheckoutShell ariaLabel={t('secureRedirect.eyebrow')} currentStep={1} hideSteps onDismiss={onDismiss}>
      <div class="sm-eyebrow sm-eyebrow-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        {t('secureRedirect.eyebrow')}
      </div>
      {body()}
    </CheckoutShell>
  );
}
